import { Button } from "@/components/primitives/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/primitives/form";
import { Input } from "@/components/primitives/input";
import { ScrollArea } from "@/components/primitives/scroll-area";
import { MAX_WORD_LENGTH, PHRASE_WORDS_COUNT } from "@/config/constants";
import { cn } from "@/lib/utils";
import {
  createRecoveryPhraseSchema,
  RecoveryPhraseType,
  RecoveryPhraseValidator,
} from "@/schemas/recoveryKey";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface PhraseFormProps<ValidationParams> {
  validator: RecoveryPhraseValidator<ValidationParams>;
  onSubmit: (data: RecoveryPhraseType) => void;
  onClose?: () => void;
}
export default function PhraseForm<ValidationParams>({
  onSubmit,
  onClose,
  validator,
}: PhraseFormProps<ValidationParams>) {
  const { t } = useTranslation();
  const form = useForm<RecoveryPhraseType>({
    resolver: zodResolver(createRecoveryPhraseSchema(validator)),
    defaultValues: {
      phrase: Array(PHRASE_WORDS_COUNT).fill(""),
    },
  });

  const handleFormPaste = (e: React.ClipboardEvent<HTMLFormElement>) => {
    const pastedText = e.clipboardData.getData("text");
    const words = pastedText.trim().replace(/\s+/g, " ").split(" ");

    if (words.length === PHRASE_WORDS_COUNT) {
      e.preventDefault();
      words.forEach((word, i) => form.setValue(`phrase.${i}`, word));
    }
  };

  return (
    <Form {...form}>
      <form
        className="flex size-full flex-col items-center gap-4"
        onSubmit={form.handleSubmit(onSubmit)}
        onPaste={handleFormPaste}
      >
        <ScrollArea className="h-[calc(100dvh-25%)] w-full">
          <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
            {Array.from({ length: PHRASE_WORDS_COUNT }).map((_, index) => (
              <FormField
                key={index}
                control={form.control}
                name={`phrase.${index}` as const}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{index + 1}</FormLabel>
                    <FormControl>
                      <Input
                        className={cn(
                          "bg-card",
                          !!form.formState.errors.phrase?.[index] &&
                            "border-destructive bg-destructive/10",
                        )}
                        maxLength={MAX_WORD_LENGTH}
                        {...field}
                        onChange={(e) => {
                          // Transform input to lowercase and trim empty spaces before updating form state
                          const lowercaseValue = e.target.value
                            .trim()
                            .toLowerCase();
                          field.onChange(lowercaseValue);
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          // get the pasted text from clipboard
                          const pastedText = e.clipboardData.getData("text");
                          // transform to lowercase and trim emptyspaces
                          const transformedValue = pastedText
                            .toLowerCase()
                            .trim();
                          // Update the field value
                          field.onChange(transformedValue);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
        </ScrollArea>
        {form.formState.errors.phrase && (
          <p className="col-span-2 text-center text-sm text-destructive">
            {form.formState.errors.phrase.root?.message}
          </p>
        )}
        <div className="flex w-full items-center justify-end gap-4">
          {onClose && (
            <Button type="button" onClick={onClose} variant="outline">
              {t("cancel")}
            </Button>
          )}
          <Button type="submit">{t("next")}</Button>
        </div>
      </form>
    </Form>
  );
}
